import React, { useContext } from 'react';
import { Box, Icon, Typography, useTheme } from '@nextiva/next-ui-components';
import { dataSelector } from '@nextiva/global-store';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useFeature } from '@nextiva/feature-toggle';
import { FeatureContext } from 'components/AccessResolvers/featureContext';
import * as UserTypes from 'types/User';
import * as LicenseTypes from 'types/License';
import * as RoleTypes from 'types/Role';
import * as LaunchpadConstants from 'constants/launchpad';
import * as LaunchpadTypes from 'types/Launchpad';
import { Launchpad } from 'components/Launchpad/Launchpad';
import { useLaunchpadQuery } from 'hooks/Launchpad/useLaunchpadQuery';
import { validatePermission, userIsSuperAdmin } from 'utils/launchpad';
import { LaunchpadLoader } from 'components/Launchpad/Loader/Loader';
import { CXChatBanner } from 'components/CXChatBanner/CXChatBanner';
import { findFeatureFromLicenseList } from 'utils/license-helpers';

export const LaunchpadContainer = () => {
  const {
    sso,
    licenses,
    roles,
    user,
  }: {
    sso: string;
    licenses: LicenseTypes.License[];
    roles: RoleTypes.Role[];
    user: UserTypes.User;
  } = useSelector(dataSelector);

  const {
    profileDTO: { corpAcctNbr },
  } = user;

  const history = useHistory();

  const theme = useTheme();

  const { featureState } = useContext(FeatureContext);
  const { isEnabled: isCXChatBannerEnabled } = useFeature({
    name: 'LaunchpadSPA.AdminDashboard.CXChatBanner',
  });

  const cxChatFeature = findFeatureFromLicenseList(licenses, 'CX_CHAT');
  const cxGrowthFeature = findFeatureFromLicenseList(
    licenses,
    'CXSocialGrowth',
  );
  const displayCXChatBanner =
    cxChatFeature && cxGrowthFeature && isCXChatBannerEnabled;

  const dataImportUrl = featureState?.bulkImports
    ? `${window.location.origin}/apps/platform-admin/#/data-import`
    : `${window.location.origin}/apps/integrations`;

  const userRoleIsSuperAdmin = userIsSuperAdmin(roles);

  const {
    isQueryLoading,
    isQueryError,
    callFlowCount,
    macroQueryData,
    userCount,
    teamCount,
    isVoiceAccount,
  } = useLaunchpadQuery(userRoleIsSuperAdmin, roles[0].name, corpAcctNbr, sso);

  const enabledTextColor = theme.palette.secondary.contrastText;

  const disabledTextColor = theme.palette.grey['500'];

  const isUsersTileEnabled = validatePermission(
    LaunchpadConstants.PERMISSION_TYPES.people.resourceId,
    macroQueryData,
    LaunchpadConstants.PERMISSION_TYPES.people.users,
    userRoleIsSuperAdmin,
  );

  const isTeamsTileEnabled = validatePermission(
    LaunchpadConstants.PERMISSION_TYPES.people.resourceId,
    macroQueryData,
    LaunchpadConstants.PERMISSION_TYPES.people.teams,
    userRoleIsSuperAdmin,
  );

  const isCallFlowsTileEnabled =
    isVoiceAccount &&
    validatePermission(
      LaunchpadConstants.PERMISSION_TYPES.communications.resourceId,
      macroQueryData,
      LaunchpadConstants.PERMISSION_TYPES.communications.voice,
      userRoleIsSuperAdmin,
    );

  const isRolesAndPermissionsEnabled = validatePermission(
    LaunchpadConstants.PERMISSION_TYPES.people.resourceId,
    macroQueryData,
    LaunchpadConstants.PERMISSION_TYPES.people.roles,
    userRoleIsSuperAdmin,
  );

  const isDataImportTileEnabled = validatePermission(
    LaunchpadConstants.PERMISSION_TYPES.data.resourceId,
    macroQueryData,
    LaunchpadConstants.PERMISSION_TYPES.data.import,
    userRoleIsSuperAdmin,
  );

  const gridTileDefs: LaunchpadTypes.GridTileDef[] = [
    {
      ...LaunchpadConstants.USERS_GRID_TILE_DEF,
      actionHandler: () => history.push('/users') as void,
      content: (
        <Typography
          variant="h5"
          sx={{
            color: isUsersTileEnabled ? enabledTextColor : disabledTextColor,
          }}
        >
          {userCount}
        </Typography>
      ),
      iconElement: (
        <Icon
          icon={LaunchpadConstants.USERS_GRID_TILE_DEF.icon as any}
          sx={{
            fontSize: LaunchpadConstants.GRID_TILE_ICON_FONT_SIZE,
            color: theme.palette.primary.dark,
          }}
        />
      ),
      isEnabled: isUsersTileEnabled,
      titleColor: isUsersTileEnabled ? enabledTextColor : disabledTextColor,
    },
    {
      ...LaunchpadConstants.TEAMS_GRID_TILE_DEF,
      actionHandler: () => history.push('/teams') as void,
      content: (
        <Typography
          variant="h5"
          sx={{
            color: isTeamsTileEnabled ? enabledTextColor : disabledTextColor,
          }}
        >
          {teamCount}
        </Typography>
      ),
      iconElement: (
        <Icon
          icon={LaunchpadConstants.TEAMS_GRID_TILE_DEF.icon as any}
          sx={{
            fontSize: LaunchpadConstants.GRID_TILE_ICON_FONT_SIZE,
            color: theme.palette.success.main,
          }}
        />
      ),
      isEnabled: isTeamsTileEnabled,
      titleColor: isTeamsTileEnabled ? enabledTextColor : disabledTextColor,
    },

    isCallFlowsTileEnabled
      ? {
          ...LaunchpadConstants.CALL_FLOW_GRID_TILE_DEF,
          actionHandler: () =>
            ((window.location.href = `${window.location.origin}/apps/voice-admin/#/voice/call-flows`) as unknown) as void,
          content: (
            <Typography
              variant="h5"
              sx={{
                color: isCallFlowsTileEnabled
                  ? enabledTextColor
                  : disabledTextColor,
              }}
            >
              {callFlowCount}
            </Typography>
          ),
          iconElement: (
            <Icon
              icon={LaunchpadConstants.CALL_FLOW_GRID_TILE_DEF.icon as any}
              sx={{
                fontSize: LaunchpadConstants.GRID_TILE_ICON_FONT_SIZE,
                color: '#FFB800', // not in theme but in Figma
                transform: 'rotate(90deg)',
              }}
            />
          ),
          isEnabled: isCallFlowsTileEnabled,
          titleColor: isCallFlowsTileEnabled
            ? enabledTextColor
            : disabledTextColor,
        }
      : {
          ...LaunchpadConstants.ROLES_GRID_TILE_DEF,
          actionHandler: () =>
            ((window.location.href = `${window.location.origin}/apps/platform-admin/#/roles`) as unknown) as void,
          content: (
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.blueGrey.A600,
                marginBottom: '12px',
              }}
            >
              {LaunchpadConstants.ROLES_GRID_TILE_DEF.content}
            </Typography>
          ),
          iconElement: (
            <Icon
              icon={LaunchpadConstants.ROLES_GRID_TILE_DEF.icon as any}
              sx={{
                fontSize: LaunchpadConstants.GRID_TILE_ICON_FONT_SIZE,
                color: theme.palette.purple[600],
              }}
            />
          ),
          isEnabled: isRolesAndPermissionsEnabled,
          titleColor: isRolesAndPermissionsEnabled
            ? enabledTextColor
            : disabledTextColor,
        },
    {
      ...LaunchpadConstants.DATA_IMPORT_GRID_TILE_DEF,
      actionHandler: () =>
        ((window.location.href = dataImportUrl) as unknown) as void,
      content: (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.blueGrey.A600,
            marginBottom: '12px',
          }}
        >
          {LaunchpadConstants.DATA_IMPORT_GRID_TILE_DEF.content}
        </Typography>
      ),
      iconElement: (
        <Icon
          icon={LaunchpadConstants.DATA_IMPORT_GRID_TILE_DEF.icon as any}
          sx={{
            fontSize: LaunchpadConstants.GRID_TILE_ICON_FONT_SIZE,
            color: theme.palette.success.dark,
          }}
        />
      ),
      isEnabled: isDataImportTileEnabled,
      titleColor: isDataImportTileEnabled
        ? enabledTextColor
        : disabledTextColor,
    },
  ];

  return (
    <>
      {isQueryLoading ? (
        <LaunchpadLoader />
      ) : (
        <>
          {displayCXChatBanner && <CXChatBanner />}
          <Box sx={{ paddingX: '60px', paddingY: '40px' }}>
            <Launchpad
              featureState={featureState}
              isQueryError={isQueryError}
              gridTileDefs={gridTileDefs}
              isVoiceAccount={isVoiceAccount}
              licenses={licenses}
              roles={roles}
              userPermissions={macroQueryData}
              user={user}
            />
          </Box>
        </>
      )}
    </>
  );
};
